import {
  Box,
  Button,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { MdImage, MdLightbulbOutline, MdOndemandVideo } from "react-icons/md";
import YouTube from "react-youtube";
import { studentPalette } from "../../../constants/studentPalette";
import { extractYouTubeVideoId } from "../../../utils/youtubeVideoId";
import { isValidQuestionAsset } from "./exam-answer-media";

const BASE_URL = "https://edutopiaonline.com/api";
const MODAL = {
  HINT: "hint",
  VIDEO: "video",
  CAMBRIDGE: "cambridge",
};

const ACADEMIC = {
  primary: studentPalette.primary,
  accent: studentPalette.secondary,
};

const youtubeOpts = {
  height: 360,
  width: 640,
  playerVars: {
    autoplay: 0,
    rel: 0,
  },
};

function isValidHtmlContent(value) {
  if (!value || value === "null" || value === "undefined") return false;
  return String(value).replace(/<[^>]*>/g, "").trim().length > 0;
}

const QuestionResourceActions = ({
  question,
  variant = "review",
  examType,
  design = "default",
  showAllButtons = false,
}) => {
  const [activeModal, setActiveModal] = useState(null);
  const isAcademic = design === "academic";
  const isMissed = design === "missed";
  const isSolve = variant === "solve";

  const hasHint = isValidQuestionAsset(question?.model_answer_image);
  const videoId = useMemo(() => extractYouTubeVideoId(question?.url), [question?.url]);
  const hasVideo = Boolean(videoId);
  const cambridgeAnswer =
    question?.cambridge_answer ?? question?.cambrige_answer;
  const hasCambridgeAnswer = isValidHtmlContent(cambridgeAnswer);
  const hasCambridgeImage = isValidQuestionAsset(question?.cambridge_image);
  const hasCambridge = hasCambridgeAnswer || hasCambridgeImage;

  const showHint = isSolve ? Number(examType) === 2 && hasHint : hasHint;
  const showVideo = !isSolve && hasVideo;
  const showCambridge = !isSolve && hasCambridge;

  const renderVideo = showAllButtons || showVideo;
  const renderHint = showAllButtons || showHint;
  const renderCambridge = showAllButtons || showCambridge;

  useEffect(() => {
    setActiveModal(null);
  }, [question?.id]);

  if (!renderVideo && !renderHint && !renderCambridge) return null;

  const buttonSx = isAcademic
    ? {
        bg: ACADEMIC.primary,
        color: "white",
        px: { base: 5, md: 8 },
        py: 5,
        borderRadius: "xl",
        fontWeight: "800",
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        fontSize: "xs",
        _hover: { bg: ACADEMIC.accent },
        boxShadow: "md",
      }
    : isMissed
      ? {
          size: "sm",
          bg: ACADEMIC.primary,
          color: studentPalette.cream,
          fontWeight: "700",
          borderRadius: "md",
          _hover: { bg: ACADEMIC.accent },
          _disabled: { opacity: 0.45, cursor: "not-allowed", _hover: { bg: ACADEMIC.primary } },
        }
      : {
          bg: "main_1",
          color: "white",
          size: "sm",
        };

  const closeModal = () => setActiveModal(null);

  const renderButton = (visible, enabled, icon, label, modalKey) => {
    if (!visible) return null;

    return (
      <Button
        leftIcon={icon}
        isDisabled={showAllButtons && !enabled}
        onClick={() => enabled && setActiveModal(modalKey)}
        {...buttonSx}
      >
        {label}
      </Button>
    );
  };

  return (
    <>
      <Flex
        w="100%"
        justify={isMissed ? "flex-start" : "flex-end"}
        gap={3}
        flexWrap="wrap"
        mb={4}
      >
        {renderButton(
          renderVideo,
          showVideo,
          <MdOndemandVideo size={18} />,
          "Video Hint",
          MODAL.VIDEO
        )}
        {renderButton(
          renderHint,
          showHint,
          <MdLightbulbOutline size={18} />,
          "Handwritten Answer",
          MODAL.HINT
        )}
        {renderButton(
          renderCambridge,
          showCambridge,
          <MdImage size={18} />,
          "Mark Scheme Answer",
          MODAL.CAMBRIDGE
        )}
      </Flex>

      <Modal isCentered isOpen={activeModal === MODAL.HINT} onClose={closeModal} motionPreset="slideInBottom">
        <ModalOverlay />
        <ModalContent maxW={{ base: "calc(100vw - 24px)", md: "xl" }}>
          <ModalHeader fontSize="md" fontWeight="800">
            Handwritten Answer
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} display="flex" justifyContent="center">
            <Image
              src={`${BASE_URL}/${question.model_answer_image}`}
              alt="Handwritten answer"
              w="100%"
              maxW={{ base: "100%", md: "480px" }}
              maxH="400px"
              objectFit="contain"
              borderRadius="md"
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isCentered isOpen={activeModal === MODAL.VIDEO} onClose={closeModal} motionPreset="slideInBottom">
        <ModalOverlay />
        <ModalContent maxW={{ base: "calc(100vw - 24px)", md: "3xl" }}>
          <ModalHeader fontSize="md" fontWeight="800">
            Video Hint
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} display="flex" justifyContent="center">
            <Box
              w="100%"
              maxW="640px"
              sx={{
                "& iframe": {
                  display: "block",
                  maxWidth: "100%",
                },
              }}
            >
              <YouTube
                videoId={videoId}
                opts={youtubeOpts}
                onReady={(event) => event.target.pauseVideo()}
              />
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isCentered isOpen={activeModal === MODAL.CAMBRIDGE} onClose={closeModal} motionPreset="slideInBottom">
        <ModalOverlay />
        <ModalContent maxW={{ base: "calc(100vw - 24px)", md: "xl" }}>
          <ModalHeader fontSize="md" fontWeight="800">
            Mark Scheme Answer
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} display="flex" justifyContent="center">
            {hasCambridgeAnswer ? (
              <Box
                w="100%"
                maxW={{ base: "100%", md: "640px" }}
                lineHeight="1.65"
                sx={{
                  "html[data-theme='dark'] &": { color: "var(--text-primary)" },
                  "html[data-theme='dark'] & p, html[data-theme='dark'] & span, html[data-theme='dark'] & div, html[data-theme='dark'] & li":
                    { color: "inherit" },
                }}
                dangerouslySetInnerHTML={{ __html: cambridgeAnswer }}
              />
            ) : (
              <Image
                src={`${BASE_URL}/${question.cambridge_image}`}
                alt="Mark scheme answer"
                w="100%"
                maxW={{ base: "100%", md: "480px" }}
                maxH="400px"
                objectFit="contain"
                borderRadius="md"
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default QuestionResourceActions;
